import React, { useState, useCallback, useRef, useMemo } from 'react';
import { LessonScreen } from './LessonScreen';
import type { LessonScreen as LessonScreenType, PassiveScreenType } from '../../types/course';

const PASSIVE_TYPES: Set<string> = new Set<PassiveScreenType>([
  'text', 'bullets', 'analogy', 'comparison', 'key-takeaways',
]);

interface Props {
  screens: LessonScreenType[];
  onComplete: () => void;
}

export const LessonCarousel: React.FC<Props> = ({ screens, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  // Track which screens the user has completed interaction on
  const [completedScreens, setCompletedScreens] = useState<Set<number>>(() => {
    // Passive screens start as completed
    const initial = new Set<number>();
    screens.forEach((s, i) => {
      if (PASSIVE_TYPES.has(s.type)) initial.add(i);
    });
    return initial;
  });

  const isLast = currentIndex === screens.length - 1;
  const isFirst = currentIndex === 0;
  const isCurrentCompleted = completedScreens.has(currentIndex);

  const goNext = useCallback(() => {
    if (!isCurrentCompleted) return;
    if (isLast) {
      onComplete();
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [isLast, onComplete, isCurrentCompleted]);

  const goPrev = useCallback(() => {
    if (!isFirst) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [isFirst]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  const handleInteractionComplete = useCallback(() => {
    setCompletedScreens((prev) => new Set(prev).add(currentIndex));
  }, [currentIndex]);

  // Can navigate to a dot? Only if all screens before it are completed (or it's a previous screen)
  const canNavigateTo = useCallback(
    (targetIndex: number) => {
      if (targetIndex <= currentIndex) return true;
      // Must have completed all screens from current up to targetIndex-1
      for (let i = currentIndex; i < targetIndex; i++) {
        if (!completedScreens.has(i)) return false;
      }
      return true;
    },
    [currentIndex, completedScreens]
  );

  const handleDotClick = useCallback(
    (i: number) => {
      if (canNavigateTo(i)) {
        setCurrentIndex(i);
      }
    },
    [canNavigateTo]
  );

  // Memoize completed boolean for current screen
  const isScreenPreviouslyCompleted = useMemo(
    () => completedScreens.has(currentIndex) && !PASSIVE_TYPES.has(screens[currentIndex].type),
    [completedScreens, currentIndex, screens]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Progress dots */}
      <div className="flex justify-center gap-1.5 mb-4">
        {screens.map((_, i) => (
          <button
            key={i}
            onClick={() => handleDotClick(i)}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              i === currentIndex
                ? 'bg-[var(--clue-green-accent)] w-4'
                : i < currentIndex
                  ? 'bg-[var(--clue-green-accent)]/50'
                  : 'bg-[var(--clue-border-mid)]'
            }`}
            aria-label={`Go to screen ${i + 1}`}
          />
        ))}
      </div>

      {/* Screen content */}
      <div
        className="flex-1 overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <LessonScreen
          key={currentIndex}
          screen={screens[currentIndex]}
          onInteractionComplete={handleInteractionComplete}
          completed={isScreenPreviouslyCompleted}
        />
      </div>

      {/* Navigation */}
      <div className="flex gap-3 mt-4 pt-4 border-t border-[var(--clue-border-mid)]">
        {!isFirst && (
          <button
            onClick={goPrev}
            className="flex-1 h-11 rounded-lg border border-[var(--clue-border-mid)] text-sm font-medium text-[var(--clue-text-secondary)] hover:bg-[var(--clue-bg-secondary)] transition-colors"
          >
            Back
          </button>
        )}
        <button
          onClick={goNext}
          disabled={!isCurrentCompleted}
          className={`flex-1 h-11 rounded-lg text-sm font-semibold transition-all duration-200 ${
            isCurrentCompleted
              ? 'bg-[var(--clue-green-accent)] text-white hover:opacity-90'
              : 'bg-[var(--clue-border-mid)] text-[var(--clue-text-tertiary)] cursor-not-allowed'
          }`}
        >
          {isLast ? 'Start Quiz' : 'Next'}
        </button>
      </div>
    </div>
  );
};
